import React, { useEffect, useState } from 'react';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { IUserJson } from '@scale/shared/types/user';
import { ChatBulkAuditBatch, AttemptAuditResults } from '../SpeedAudit/types';
import ChatBulkAuditHeader from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditHeader';
import _ from 'lodash';
import ChatBulkAuditResultsPage from 'react-scale/components/audits/ChatBulkAudit/ChatBulkAuditResultsPage';
import { ScaleLogoLoading } from '@scale/scaleui';
import { QAOperationContextProvider } from 'react-scale/components/remoAdmin/qaOperation/qaOperationContext';
import { QAOperationJson } from '@scale/shared/types/qaOperation/qaOperation';
import { getRelatedQaOperationForAuditBatch } from './ChatBulkAuditApi';

interface ChatBulkAuditResultsProps {
  auditBatchId?: string;
  user: IUserJson;
  handleComplete?: () => void;
}

function ChatBulkAuditResults(props: ChatBulkAuditResultsProps) {
  const [auditBatch, setAuditBatch] = useState<ChatBulkAuditBatch | null>(null);
  const [attemptAudits, setAttemptAudits] = useState<AttemptAuditResults[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [relatedQaOperation, setRelatedQaOperation] = useState<QAOperationJson | undefined>(
    undefined,
  );

  const fetchAuditBatch = async (auditBatchId: string) => {
    const result = await apiFetch(null, `corp-api/chatBulkAudit/batch/${auditBatchId}`, {
      method: 'GET',
    });
    setAuditBatch(result);

    const audits = await apiFetch(
      null,
      `corp-api/chatBulkAudit/attemptAudit/${auditBatchId}/results`,
      {
        method: 'GET',
      },
    );
    setAttemptAudits(audits);
    setIsLoading(false);
  };

  const fetchRelatedQaOperation = async (auditBatchId: string) => {
    const result = await getRelatedQaOperationForAuditBatch(auditBatchId);
    setRelatedQaOperation(result);
  };

  useEffect(() => {
    if (props.auditBatchId) {
      fetchAuditBatch(props.auditBatchId);
    } else {
      setIsLoading(false);
    }
  }, [props.auditBatchId]);

  useEffect(() => {
    if (props.auditBatchId) {
      fetchRelatedQaOperation(props.auditBatchId);
    }
  }, [props.auditBatchId]);

  return (
    <div className="scaleui scale-tailwind scale-theme">
      <div className="fixed top-0 left-0 w-screen h-screen bg-neutral-50 z-50 overflow-y-auto">
        {auditBatch && (
          <div className="flex flex-col h-full">
            <ChatBulkAuditHeader
              auditBatch={auditBatch}
              auditorHasClaim={false} // edits not required in this view
              allowEdits={false}
              formattedResponseView={false}
              setFormattedResponseView={() => {}}
              setAllowEdits={() => {}}
              resultsPage={true}
            />
            {isLoading && (
              <div className="items-center justify-center flex-1 flex">
                <ScaleLogoLoading className="" size="lg" />
              </div>
            )}
            {!_.isEmpty(attemptAudits) && (
              <QAOperationContextProvider
                initialQAOperation={relatedQaOperation}
                attemptResult={attemptAudits}
              >
                <ChatBulkAuditResultsPage
                  handleComplete={props.handleComplete}
                  auditBatch={auditBatch}
                  attemptAuditResultsLocalCopy={attemptAudits}
                  user={props.user}
                />
              </QAOperationContextProvider>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default React.memo(ChatBulkAuditResults);
